import { ArrowRight, ArrowUpRight, Check, Server, RefreshCw, Mail } from "lucide-react";
import { useLeadModal } from "../../context/LeadModalContext";

const plans = [
    {
        name: "Bot Blueprint Call",
        price: "$297",
        sub: "60-min strategy session",
        features: [
            "Live audit of one workflow ready for automation",
            "Custom build plan with tools, scope, and timeline",
            "Recorded Loom recap you keep forever",
            "$297 credited toward any build you commission within 30 days",
        ],
        cta: "Book a Blueprint Call",
        source: "pricing-blueprint",
        featured: false,
    },
    {
        name: "Starter Bot",
        price: "From $2,500",
        sub: "Fixed scope. 2-week delivery.",
        features: [
            "One workflow fully automated end-to-end",
            "Lead capture, intake, FAQ, scheduling, or support bot",
            "Connected to the tools you already use",
            "30 days of post-launch support included",
        ],
        cta: "Scope a Starter Bot",
        source: "pricing-starter",
        featured: true,
        badge: "Most Popular",
    },
    {
        name: "Custom App Build",
        price: "From $7,500",
        sub: "Full-stack. 4–6 weeks.",
        features: [
            "Custom AI-powered web app (think: ezScribe-scale builds)",
            "Auth, database, Stripe billing, hosted deployment",
            "Built on your domain, owned by you",
        ],
        cta: "Scope a Custom App",
        source: "pricing-custom",
        featured: false,
    },
];

export default function PricingPreview({ standalone = false }) {
    const { openModal } = useLeadModal();
    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className={`relative ${standalone ? "pt-16 pb-24" : "py-24 sm:py-32"} scroll-mt-nav`}
        >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00f0ff]/30 to-transparent" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#00f0ff]/[0.06] blur-[120px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
                {!standalone && (
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00f0ff]">
                            Pricing
                        </span>
                        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mt-4 leading-[1.05]">
                            Real pricing.{" "}
                            <span className="text-gradient-cyan">No &ldquo;contact us&rdquo; runaround.</span>
                        </h2>
                        <p className="text-lg text-[#94a3b8] mt-6">
                            Every project is scoped and fixed-price after a free 30-minute call.
                            No hourly billing, no scope-creep invoices, no surprises. Here&apos;s
                            where most builds land:
                        </p>
                    </div>
                )}

                {/* Three pricing cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto items-stretch">
                    {plans.map((plan) => (
                        <PricingCard key={plan.name} plan={plan} onSelect={openModal} />
                    ))}
                </div>

                {/* Reassurance line */}
                <p className="text-center text-[#94a3b8] mt-12 max-w-2xl mx-auto leading-relaxed">
                    Not sure which fits? Start with a Blueprint Call. We&apos;ll tell you exactly
                    what you need — even if the answer is &ldquo;you don&apos;t need us
                    yet.&rdquo;
                </p>

                {/* Hosting & ongoing support */}
                <div className="mt-16 max-w-5xl mx-auto">
                    <div className="text-center mb-8">
                        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00f0ff]">
                            Keep it running
                        </span>
                        <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter mt-3">
                            Hosting &amp; ongoing support
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
                        {/* Hosting */}
                        <article
                            data-testid="pricing-hosting-card"
                            className="flex flex-col rounded-2xl bg-[#0a1325] border border-white/10 p-8 hover:border-[#00f0ff]/40 transition-colors"
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-11 h-11 rounded-lg bg-[#00f0ff]/10 border border-[#00f0ff]/30 flex items-center justify-center">
                                    <Server className="w-5 h-5 text-[#00f0ff]" />
                                </div>
                                <h4 className="font-heading text-xl font-bold tracking-tight text-white">
                                    App Hosting
                                </h4>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                                <span className="font-heading text-4xl font-black tracking-tighter text-white">
                                    $125
                                </span>
                                <span className="text-sm text-[#94a3b8]">/ month</span>
                            </div>
                            <p className="text-sm text-[#94a3b8] leading-relaxed mt-4">
                                We host your web app and handle basic maintenance tasks to keep it
                                secure, updated, and running smoothly.
                            </p>
                        </article>

                        {/* Monthly retainer */}
                        <article
                            data-testid="pricing-retainer-card"
                            className="flex flex-col rounded-2xl bg-gradient-to-b from-[#0a1325] to-[#021b3a] border border-[#00f0ff]/40 p-8"
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-11 h-11 rounded-lg bg-[#00f0ff]/10 border border-[#00f0ff]/30 flex items-center justify-center">
                                    <RefreshCw className="w-5 h-5 text-[#00f0ff]" />
                                </div>
                                <h4 className="font-heading text-xl font-bold tracking-tight text-white">
                                    Monthly Retainer
                                </h4>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                                <span className="font-heading text-4xl font-black tracking-tighter text-white">
                                    From $500
                                </span>
                                <span className="text-sm text-[#94a3b8]">/ month</span>
                            </div>
                            <p className="text-sm text-[#94a3b8] leading-relaxed mt-4">
                                Full retainers include hosting, ongoing support, and app iteration.
                                Plans start with 2 app changes and/or updates per month — customized
                                options available.
                            </p>
                            <button
                                onClick={() => openModal("pricing-retainer")}
                                data-testid="pricing-retainer-email"
                                className="group mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold hover:bg-white transition-colors"
                            >
                                <Mail className="w-4 h-4" />
                                Email to schedule
                            </button>
                        </article>
                    </div>
                </div>

                {/* Secondary quote card */}
                <div
                    data-testid="pricing-contact-card"
                    className="relative max-w-3xl mx-auto mt-16 rounded-3xl bg-gradient-to-br from-[#0a1325] via-[#0a1325] to-[#021b3a] border border-[#00f0ff]/30 overflow-hidden"
                >
                    <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-[#1d4ed8]/15 blur-3xl pointer-events-none" />

                    <div className="relative p-10 sm:p-12 text-center">
                        <h3 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter leading-[1]">
                            Have something bigger in mind?
                        </h3>
                        <p className="mt-4 max-w-xl mx-auto text-[#94a3b8]">
                            Tell us what you&apos;re building and we&apos;ll send back a fixed,
                            written proposal within one business day — scope, timeline, and price.
                        </p>
                        <div className="mt-8 flex justify-center">
                            <button
                                onClick={() => openModal("pricing-quote")}
                                data-testid="pricing-cta-quote"
                                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold hover:bg-white transition-colors"
                            >
                                Request a custom quote
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function PricingCard({ plan, onSelect }) {
    const { name, price, sub, features, cta, source, featured, badge } = plan;
    return (
        <article
            data-testid={`pricing-card-${source}`}
            className={`relative flex flex-col rounded-2xl p-8 transition-all duration-500 hover:-translate-y-1 ${
                featured
                    ? "bg-gradient-to-b from-[#0a1325] to-[#021b3a] border border-[#00f0ff]/50"
                    : "bg-[#0a1325] border border-white/10 hover:border-[#00f0ff]/40"
            }`}
        >
            {badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#00f0ff] text-[#050b14] text-[10px] font-bold uppercase tracking-[0.18em]">
                    {badge}
                </span>
            )}

            <h3 className="font-heading text-xl font-bold tracking-tight text-white">
                {name}
            </h3>
            <div className="mt-4">
                <div className="font-heading text-4xl font-black tracking-tighter text-white">
                    {price}
                </div>
                <div className="text-sm text-[#94a3b8] mt-1.5">{sub}</div>
            </div>

            <ul className="mt-7 space-y-3 flex-1">
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#cbd5e1]">
                        <Check className="w-4 h-4 text-[#00f0ff] mt-0.5 shrink-0" strokeWidth={2.5} />
                        <span className="leading-relaxed">{f}</span>
                    </li>
                ))}
            </ul>

            <button
                onClick={() => onSelect(source)}
                data-testid={`pricing-cta-${source}`}
                className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-bold transition-all duration-300 ${
                    featured
                        ? "bg-[#00f0ff] text-[#050b14] hover:bg-white"
                        : "border border-white/15 text-white hover:border-[#00f0ff] hover:text-[#00f0ff]"
                }`}
            >
                {cta}
                <ArrowUpRight className="w-4 h-4" />
            </button>
        </article>
    );
}
